import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import type { UpdateProfessionalPayload } from '@klaro7/shared-types';
import { toast } from 'sonner';

import {
  getMyProfessionalProfile,
  getMyDashboardStats,
  updateMyProfessionalProfile,
  setMyIban,
  uploadAvatar,
  changePassword,
  updateMyUserProfile,
  submitForReview,
} from '../services/professional-profile.service';
import { extractErrorMessage } from '@/lib/api';

export function useMyProfessionalProfile() {
  return useQuery({
    queryKey: ['professional-profile', 'me'],
    queryFn: getMyProfessionalProfile,
  });
}

/** Live numbers for the consultant home (consulenze, guadagni, rating, recensioni). */
export function useMyDashboardStats() {
  return useQuery({
    queryKey: ['professional-profile', 'me', 'stats'],
    queryFn: getMyDashboardStats,
  });
}

export function useUpdateProfessionalProfile() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (data: UpdateProfessionalPayload) => updateMyProfessionalProfile(data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['professional-profile', 'me'] });
    },
  });
}

export function useSetMyIban() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (iban: string) => setMyIban(iban),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['professional-profile', 'me'] });
    },
  });
}

export function useUploadAvatar() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (file: File) => uploadAvatar(file),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['professional-profile', 'me'] });
    },
  });
}

export function useChangePassword() {
  return useMutation({
    mutationFn: ({ currentPassword, newPassword }: { currentPassword: string; newPassword: string }) =>
      changePassword(currentPassword, newPassword),
  });
}

export function useUpdateMyUserProfile() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (data: { firstName?: string; lastName?: string; artFirstName?: string; artLastName?: string; phone?: string; bio?: string }) =>
      updateMyUserProfile(data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['professional-profile', 'me'] });
    },
  });
}

export function useSubmitForReview() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: () => submitForReview(),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['professional-profile', 'me'] });
      toast.success('Profilo inviato per revisione. Ti avviseremo dopo il controllo.');
    },
    onError: (err) => toast.error(extractErrorMessage(err)),
  });
}
